import React, { useEffect, useState } from "react";
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getAllNotificationsAPI, deleteAllNotificationsAPI } from '../../utils/api';
import { setNotificationCount } from '../../store/slices/countState';
import Sidebar from "../Sidebar/Sidebar";
import Toast from '../../components/Toast/Toast';
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import "./Notification.css";

const Notification = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [toast, setToast] = useState({ show: false, message: "", type: "" });
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const PAGE_LIMIT = 10;

    const showToast = (message, type = "success") => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: "", type: "" }), 3000);
    };

    const fetchNotifications = async (currentPage = 1) => {
        try {
            setLoading(true);
            setError(null);
            const response = await getAllNotificationsAPI({
                page: currentPage,
                limit: PAGE_LIMIT,
            })
            if (response?.status === 200) {
                const list = response.data;
                const totalPagesFromApi = response?.pagination?.total_pages || 1;
                const currentPageFromApi = response?.pagination?.current_page || currentPage;
                
                setNotifications(list);
                setTotalPages(totalPagesFromApi);
                setPage(currentPageFromApi);
            } else {
                setError(response?.message || 'Failed to fetch notifications');
                setNotifications([]);
            }
        } catch (err) {
            console.log('Fetch error:', err);
            setError('Failed to fetch notifications. Please try again.');
            setNotifications([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchNotifications(page);
    }, [page]);

    const totalEntries = notifications.length;
    const safePage = Math.min(page, totalPages);
    const startIdx = (safePage - 1) * PAGE_LIMIT;
    const endIdx = startIdx + notifications.length;

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
    };

    const handleDeleteAllClick = () => {
        setShowDeleteModal(true);
    };

    const handleDeleteAllCancel = () => {
        setShowDeleteModal(false);
    };

    const handleDeleteAllConfirm = async () => {
        try {
            const response = await deleteAllNotificationsAPI();
            
            if (response?.status === 200) {
                setShowDeleteModal(false);
                showToast("All notifications deleted successfully!", "success");
                dispatch(setNotificationCount(0));
                await fetchNotifications(page); // Refresh the list
            } else if (response?.message) {
                showToast(typeof response?.message === 'string'
                    ? response?.message
                    : 'Failed to delete notifications', "error");
            } else {
                showToast('Failed to delete notifications', "error");
            }
        } catch (err) {
            showToast('Failed to delete notifications', "error");
        }
    };

    const getPageItems = () => {
        const items = [];
        const current = Math.max(1, Math.min(page, totalPages));
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) items.push(i);
            return items;
        }

        items.push(1);
        const showLeftDots = current > 4;
        const showRightDots = current < totalPages - 3;

        if (showLeftDots) items.push('dots-left');

        const start = Math.max(2, current - 1);
        const end = Math.min(totalPages - 1, current + 1);
        for (let i = start; i <= end; i++) items.push(i);

        if (showRightDots) items.push('dots-right');

        items.push(totalPages);
        return items;
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleNotificationClick = (notification) => {
        if (notification?.order_id) {
            navigate(`/order-details/${notification.order_id}`);
        }
    };

    return (
        <>
            <Toast toast={toast} />

            <div className="Notification-layout">
                <Sidebar />
                <div className="Notification-main-content">
                    {totalEntries > 0 && (
                        <div className="Notification-header-row">
                            <button
                                type="button"
                                className="insert-btn"
                                onClick={handleDeleteAllClick}
                                aria-label="Delete All Notifications"
                            >
                                <span>Delete All Notifications</span>
                            </button>
                        </div>
                    )}

                    {loading ? (
                        <div className="loading-container">
                            <div className="loading-spinner"></div>
                            <p>Loading notifications...</p>
                        </div>
                    ) : error ? (
                        <div className="loading-container">
                            <div style={{ color: '#dc2626', fontSize: '1.25rem', marginBottom: '1rem' }}>⚠️</div>
                            <p style={{ color: '#dc2626', fontSize: '1.125rem' }}>{error}</p>
                        </div>
                    ) : totalEntries === 0 ? (
                        <div className="empty-state">
                            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔔</div>
                            <h3>No Notifications</h3>
                            <p>You don't have any notifications yet.</p>
                        </div>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <table className="Notification-table">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Title</th>
                                            <th>Description</th>
                                            <th>Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {notifications.map((notification, idx) => (
                                            <tr 
                                                key={notification?._id || `notification-${idx}`}
                                                className={notification?.order_id ? 'clickable-row' : ''}
                                                onClick={() => handleNotificationClick(notification)}
                                                style={{ cursor: notification?.order_id ? 'pointer' : 'default' }}
                                            >
                                                <td>{startIdx + idx + 1}</td>
                                                <td>{notification?.title || '---'}</td>
                                                <td>{notification?.body || '---'}</td>
                                                <td>{formatDate(notification?.created_at)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="table-footer">
                                <div className="footer-left">
                                    <span>
                                        Showing {totalEntries > 0 ? startIdx + 1 : 0} to {endIdx} of {totalEntries} entries • Page {page} of {totalPages}
                                    </span>
                                </div>
                                <div className="pagination">
                                    <button
                                        type="button"
                                        disabled={page === 1}
                                        onClick={() => handlePageChange(page - 1)}
                                        aria-label="Previous page"
                                    >
                                        Previous
                                    </button>
                                    {getPageItems().map((item, idx) => (
                                        typeof item === 'number' ? (
                                            <button
                                                type="button"
                                                key={`p-${item}`}
                                                className={page === item ? 'active' : ''}
                                                onClick={() => handlePageChange(item)}
                                                aria-current={page === item ? 'page' : undefined}
                                            >
                                                {item}
                                            </button>
                                        ) : (
                                            <span key={`dots-${idx}`} className="ellipsis" aria-hidden="true">…</span>
                                        )
                                    ))}
                                    <button
                                        type="button"
                                        disabled={page === totalPages}
                                        onClick={() => handlePageChange(page + 1)}
                                        aria-label="Next page"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        </>
                    )}

                    <ConfirmationModal
                        visible={showDeleteModal}
                        onConfirm={handleDeleteAllConfirm}
                        onCancel={handleDeleteAllCancel}
                        title="Confirm Deletion"
                        message="Are you sure you want to delete all notifications? This action cannot be undone."
                        confirmText="Delete All"
                    />
                </div>
            </div>
        </>
    );
};

export default Notification;
